import type { InvoiceConfig } from "@/interfaces/invoiceConfig";
import type { Club } from "@/interfaces/club";
import { defaultInvoiceConfig } from "./invoice/default";
import { onefitInvoiceConfig } from "./invoice/onefit";
import { ladysharmonyInvoiceConfig } from "./invoice/ladysharmony";
import { trans4mInvoiceConfig } from "./invoice/trans4m";

/**
 * Registry mapping club slug to invoice configuration.
 * Add new club configs here when creating config files.
 * Config filename must match club slug (e.g. ClubA → cluba.ts).
 */
export const invoiceConfigRegistry: Record<string, InvoiceConfig> = {
  default: defaultInvoiceConfig,
  onefit: onefitInvoiceConfig,
  ladysharmony: ladysharmonyInvoiceConfig,
  trans4m: trans4mInvoiceConfig,
};

/**
 * Get invoice configuration for a club.
 * Uses club slug to find the config; falls back to default if not found.
 *
 * @param club - The club object (must have slug property)
 * @returns Invoice config for the club
 */
export function getInvoiceConfig(club: Club | null | undefined): InvoiceConfig {
  if (!club?.slug) {
    return defaultInvoiceConfig;
  }
  const slug =  slugToKey(club.slug);   
  return invoiceConfigRegistry[slug] ?? defaultInvoiceConfig;
}

/**
 * Normalize club slug for registry lookup (lowercase, trim).
 */
function slugToKey(slug: string | null): string {
  if (!slug || typeof slug !== "string") return "default";
  return slug.trim().toLowerCase();
}

/**
 * Get the logo path to use for invoice image loading.
 * Returns config.logo if set (full URL or path relative to VUE_APP_URL),
 * otherwise "club-logo/" + club.path_logo for default club logo.
 */
export function getInvoiceLogoPath(club: Club | null | undefined): string {
  const config = getInvoiceConfig(club);
  if (config.logo) {
    return config.logo.startsWith("http") ? config.logo : config.logo;
  }
  const pathLogo = club?.path_logo ?? "";
  return pathLogo ? `club-logo/${pathLogo}` : "";
}

export function getInvoiceSignaturePath(club: Club | null | undefined): string {
  const config = getInvoiceConfig(club);
  if (config.signaturePath) {
    return config.signaturePath.startsWith("http") ? config.signaturePath : config.signaturePath;
  }
  const pathSignature = club?.path_signature ?? "";
  return pathSignature ? `club-logo/${pathSignature}` : "";
}
